import React, { useEffect, useRef } from 'react';
import { useCompLookups } from '../../../../common/useCompLookups';
import TablePollingComponent from '../../shared/TablePollingComponent';
import logger from '../../../../utils/logger';

/**
 * Summary case table polling component - uses unified TablePollingComponent
 * Handles comp lookup work completion and version increments separately
 */
const SummaryCaseTablePollingComponent = ({ 
  hasLoadingRows,
  handleUpdateStateCallback,
  setNegotiationObjVersion,
  townView
}) => {
  const versionSetterRef = useRef(setNegotiationObjVersion);
  const { hasActiveWork } = useCompLookups();
  const previousActiveWorkStateRef = useRef(hasActiveWork());

  // Update ref when callback changes
  useEffect(() => {
    versionSetterRef.current = setNegotiationObjVersion;
  }, [setNegotiationObjVersion]);

  // Watch for comp lookup work completion - trigger final refresh when all work is done
  useEffect(() => {
    const currentActiveWork = hasActiveWork();
    // Trigger final refresh when comp lookup work completes
    if (previousActiveWorkStateRef.current && !currentActiveWork) {
      logger.log('🔄 All comp lookup work complete, forcing final table refresh');
      if (versionSetterRef.current) {
        versionSetterRef.current(prev => prev + 1);
      }
    }
    previousActiveWorkStateRef.current = currentActiveWork;
  }, [hasActiveWork]);

  // Skip polling in town view (no streaming updates)
  if (townView) {
    return null;
  }

  return (
    <TablePollingComponent
      shouldPoll={() => {
        const currentIsActive = hasActiveWork();
        // Stop polling if work is complete and no loading rows
        if (!currentIsActive && !hasLoadingRows) {
          // Work complete and no loading rows, force refresh via version increment
          if (versionSetterRef.current) {
            versionSetterRef.current(prev => prev + 1);
          }
          return false;
        }
        return hasLoadingRows || currentIsActive;
      }}
      onPollTick={() => {
        // Call the update callback - it will update the store if there are changes
        // The SummaryCaseTable component already has logic to increment
        // negotiationObjVersion when cases actually change
        // This prevents unnecessary re-renders when polling but no new data
        if (handleUpdateStateCallback) {
          handleUpdateStateCallback();
        }
      }}
      interval={100}
      name="SummaryCaseTablePolling"
      enableLogging={false}
      compareData={false}
    />
  );
};

export default React.memo(SummaryCaseTablePollingComponent);

